import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ChevronLeft,
  ChevronRight,
  Heart,
  MessageCircle,
  Phone,
} from "lucide-react";
import { AuthenticatedLayout } from "@/components/layout/AuthenticatedLayout";

// Type for place details
type PlaceDetails = {
  id: string;
  name: string;
  likes: number;
  address: string;
  location: string;
  openTime: string;
  openHours: { day: string; hours: string }[];
  description: string;
  website: string;
  brandStory: string;
  hometown: string;
  images: string[];
  cover: string;
  avatar: string;
  awards: number;
  feedbackPlaceholder: string;
};

// Mock data chi tiết địa điểm
const placeDetailsMock: Record<string, PlaceDetails> = {
  "Bánh mì pate": {
    id: "banh-mi-pate",
    name: "Bánh mì pate",
    likes: 11,
    address: "Quán bánh mì số 1 Hà Nội",
    location: "Hai Bà Trưng, Hà Nội",
    openTime: "5:00 PM",
    openHours: [
      { day: "Thứ hai", hours: "8:00 AM-5:00 PM" },
      { day: "Thứ ba", hours: "8:00 AM-5:00 PM" },
      { day: "Thứ tư", hours: "8:00 AM-5:00 PM" },
      { day: "Thứ năm", hours: "8:00 AM-5:00 PM" },
      { day: "Thứ sáu", hours: "8:00 AM-5:00 PM" },
      { day: "Thứ bảy", hours: "8:00 AM-5:00 PM" },
      { day: "Chủ nhật", hours: "8:00 AM-5:00 PM" },
    ],
    description: "Giao hàng tận nơi",
    website: "https://banhmipate.vn",
    brandStory: "Cập nhật danh sách hàng xóm",
    hometown: "Mình cần mượn bàn ủi đến chiều nay, ai giúp được không ạ?",
    images: ["/images/banhmi_bg.png", "/images/banhmi-item.png"],
    cover: "/images/banhmi_bg.png",
    avatar: "/images/banhmi_avt.svg",
    awards: 2,
    feedbackPlaceholder: "Gửi góp ý của bạn...",
  },
};

export default function PlaceDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  // Giả lập lấy dữ liệu từ mock theo tên (decodeURIComponent)
  const place =
    placeDetailsMock[decodeURIComponent(id || "")] ||
    placeDetailsMock["Bánh mì pate"];
  const [showOpenHours, setShowOpenHours] = useState(false);
  const [feedback, setFeedback] = useState("");

  return (
    <AuthenticatedLayout>
      <div className="max-w-md mx-auto pb-20 font-sora">
        {/* Cover + avatar + share */}
        <div className="relative">
          <img
            src={place.cover}
            alt={place.name}
            className="w-full h-40 object-cover rounded-b-xl"
          />
          <button
            className="absolute top-3 left-3 bg-white rounded-full p-2 shadow bg-[#F4E7DA]"
            onClick={() => navigate(-1)}
          >
            <ChevronLeft className="h-6 w-6 text-[#1E3A3A]" />
          </button>
          <button className="absolute top-3 right-3 bg-white rounded-full p-2 shadow bg-[#F4E7DA]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
            >
              <path
                d="M17.1663 10.833V15.833C17.1663 16.7535 16.4202 17.4997 15.4997 17.4997H5.49967C4.5792 17.4997 3.83301 16.7535 3.83301 15.833V10.833"
                stroke="#1E3A3A"
                stroke-width="1.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
              <path
                d="M10.4997 12.5V2.5M10.4997 2.5L7.58301 5.41667M10.4997 2.5L13.4163 5.41667"
                stroke="#1E3A3A"
                stroke-width="1.2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>
          <img
            src={place.avatar}
            alt={place.name}
            className="absolute left-4 -bottom-7 w-14 h-14 rounded-full border-4 border-white object-cover shadow"
          />
        </div>
        <div className="bg-[#FBF6F1] rounded-b-xl pt-10 px-4 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-xl font-semibold text-[#1E3A3A] flex items-center gap-1">
                {place.name}
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="14"
                  height="14"
                  viewBox="0 0 14 14"
                  fill="none"
                >
                  <path
                    fill-rule="evenodd"
                    clip-rule="evenodd"
                    d="M7.00027 2.29966C7.68615 1.66759 8.57935 1.3125 9.51175 1.3125C10.5186 1.3125 11.4797 1.72653 12.1835 2.45626C12.8822 3.18022 13.2712 4.15613 13.2712 5.16963C13.2712 6.18316 12.8821 7.15913 12.1835 7.88299C11.7189 8.36459 11.255 8.8571 10.7888 9.35218C9.84174 10.3578 8.88455 11.3741 7.89026 12.3317L7.88793 12.3339C7.37517 12.8204 6.56312 12.8028 6.07224 12.294L1.81682 7.88293C0.36705 6.38015 0.36705 3.95912 1.81682 2.45633C3.23552 0.98574 5.52005 0.93352 7.00027 2.29966Z"
                    fill="#1E3A3A"
                  />
                </svg>
                <span className="text-[12px] text-[#1E3A3A]">
                  {" "}
                  {place.likes}
                </span>
              </div>
              <div className="text-xs text-[#6F6F6B] mt-1">{place.address}</div>
            </div>
            <button className="w-12 h-12 flex items-center justify-center rounded-full  bg-[#215E60]">
              <Heart className="h-6 w-6 text-[#F4E7DA]" />
            </button>
          </div>
          <div className="flex gap-2 mt-4 mb-2">
            <button className="flex-1 bg-[#ECECEC] rounded-full py-2 flex items-center justify-center gap-2 text-[#1E3A3A] font-medium">
              <MessageCircle className="h-5 w-5" /> Nhắn tin
            </button>
            <button className="flex-1 bg-[#ECECEC] rounded-full py-2 flex items-center justify-center gap-2 text-[#1E3A3A] font-medium">
              <Phone className="h-5 w-5" /> Gọi
            </button>
          </div>
          {/* Dropdown giờ mở cửa */}
          <div className="mt-2 mb-2">
            <button
              className="flex items-center gap-2 text-[#1E3A3A] text-[14px] font-normal"
              onClick={() => setShowOpenHours((v) => !v)}
            >
              <img src="/icons/clock.svg" alt="clock" className="w-4 h-4" />
              Mở cửa từ {place.openTime}
              <ChevronRight
                className={`h-4 w-4 ml-1 transition-transform ${showOpenHours ? "rotate-90" : "rotate-0"}`}
              />
            </button>
            {showOpenHours && (
              <div className="pl-8 pt-2 text-[14px] text-[#1E3A3A]">
                {place.openHours.map(
                  (item: { day: string; hours: string }, idx: number) => (
                    <div key={idx} className="flex justify-between w-48">
                      <span>{item.day}</span>
                      <span>{item.hours}</span>
                    </div>
                  ),
                )}
              </div>
            )}
          </div>
          <div className="flex items-center gap-2 text-[14px] text-[#1E3A3A] mb-2">
            <img src="/icons/map.svg" alt="map" className="w-4 h-4" />
            {place.location}
          </div>
          {/* Mô tả */}
          <div className="mt-4">
            <div className="font-semibold text-[#1E3A3A] mb-1">Mô tả</div>
            <div className="flex items-center gap-2 text-sm text-[#1E3A3A] mb-1">
              <img src="/icons/clock.svg" alt="clock" className="w-4 h-4" />
              {place.description}
            </div>
            <img
              src="/icons/link_profile.svg"
              alt="link-website"
              className="inline-block align-middle mr-2 w-4 h-4"
            />
            <a
              href={place.website}
              className="text-[#215E60] underline text-sm"
              target="_blank"
              rel="noopener noreferrer"
            >
              Ghé trang web của chúng tôi
            </a>
          </div>
          {/* Câu chuyện thương hiệu */}
          <div className="mt-4">
            <div className="font-semibold text-[#1E3A3A] mb-1">
              Câu chuyện thương hiệu
            </div>
            <button className="border border-[#1E3A3A] rounded-full px-4 py-2 text-[#1E3A3A] text-sm">
              {place.brandStory}
            </button>
          </div>
          {/* Quê quán */}
          <div className="mt-4">
            <div className="font-semibold text-[#1E3A3A] mb-1">Quê quán</div>
            <div className="text-sm text-[#1E3A3A]">{place.hometown}</div>
          </div>
          {/* Ảnh */}
          <div className="mt-4">
            <div className="font-semibold text-[#1E3A3A] mb-1">Ảnh</div>
            <div className="w-full rounded-xl overflow-hidden">
              <img
                src={place.images[0]}
                alt="Ảnh"
                className="w-full h-40 object-cover"
              />
            </div>
          </div>
          {/* Giải thưởng */}
          <div className="mt-4">
            <div className="font-semibold text-[#1E3A3A] mb-1">Giải thưởng</div>
            <div className="flex gap-2">
              <img src="/icons/award.svg" alt="award" className="w-15 h-15" />
              <img src="/icons/award.svg" alt="award" className="w-15 h-15" />
            </div>
          </div>
          {/* Góp ý */}
          <div className="mt-4">
            <div className="font-semibold text-[#1E3A3A] mb-1">Góp ý</div>
            <div className="flex items-center bg-white rounded-xl px-3 py-2 border border-[#ECECEC]">
              <img
                src="/images/avt.svg"
                alt="feedback"
                className="w-7 h-7 mr-2"
              />
              <input
                type="text"
                placeholder={place.feedbackPlaceholder}
                className="flex-1 bg-transparent outline-none text-sm text-[#1E3A3A]"
                value={feedback}
                onChange={(e) => setFeedback(e.target.value)}
              />
              <button className="ml-2">
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M21 12L3 4V10L12 12L3 14V20L21 12Z"
                    stroke="#6F6F6B"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </AuthenticatedLayout>
  );
}
